import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import './Home.css';

export default function HomePage() {
  const { t } = useLanguage();

  const getTranslatedItems = (keyPrefix, count) => {
    return Array.from({ length: count }).map((_, i) => t(`${keyPrefix}.${i}`));
  };

  const STATS = [
    { value: '1,200m', label: t('home.stats.altitude') },
    { value: '4.9', label: t('home.stats.guestRating') },
    { value: '120+', label: t('home.stats.happyGuests') },
    { value: '365', label: t('home.stats.daysOpen') },
  ];

  const AMENITIES = [
    {
      title: t('home.amenities.list.0.title'),
      desc: t('home.amenities.list.0.desc'),
      detail: t('home.amenities.list.0.detail'),
      svg: 'M4 18L10 8L14 14L18 6L22 18',
    },
    {
      title: t('home.amenities.list.1.title'),
      desc: t('home.amenities.list.1.desc'),
      detail: t('home.amenities.list.1.detail'),
      svg: 'M2 12C2 12 5 8 8 8C11 8 11 12 14 12C17 12 17 8 20 8C22 8 22 12 22 12M4 16C4 16 6 14 8 14C10 14 10 16 12 16C14 16 14 14 16 14C18 14 20 16 20 16',
    },
    {
      title: t('home.amenities.list.2.title'),
      desc: t('home.amenities.list.2.desc'),
      detail: t('home.amenities.list.2.detail'),
      svg: 'M12 2C6 8 4 12 4 15C4 19.4 7.6 22 12 22C16.4 22 20 19.4 20 15C20 12 18 8 12 2ZM12 19C9.8 19 8 17.2 8 15C8 13 10 10 12 7C14 10 16 13 16 15C16 17.2 14.2 19 12 19Z',
    },
    {
      title: t('home.amenities.list.3.title'),
      desc: t('home.amenities.list.3.desc'),
      detail: t('home.amenities.list.3.detail'),
      svg: 'M12 2L8 8H4L8 14H2L12 22L22 14H16L20 8H16L12 2Z',
    },
    {
      title: t('home.amenities.list.4.title'),
      desc: t('home.amenities.list.4.desc'),
      detail: t('home.amenities.list.4.detail'),
      svg: 'M3 12H21M5 12C5 7.6 8.1 4 12 4C15.9 4 19 7.6 19 12M7 12V14C7 17 9 20 12 20C15 20 17 17 17 14V12',
    },
    {
      title: t('home.amenities.list.5.title'),
      desc: t('home.amenities.list.5.desc'),
      detail: t('home.amenities.list.5.detail'),
      svg: 'M12 2L14.4 8.6L21.5 9.2L16 14L17.5 21L12 17.5L6.5 21L8 14L2.5 9.2L9.6 8.6L12 2Z',
    },
  ];

  const EXPERIENCES = [
    t('home.experiences.list.0'),
    t('home.experiences.list.1'),
    t('home.experiences.list.2'),
    t('home.experiences.list.3'),
    t('home.experiences.list.4'),
    t('home.experiences.list.5'),
  ];

  useEffect(() => {
    const reveals = document.querySelectorAll('[data-reveal]');
    if (!reveals.length) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('revealed'); obs.unobserve(e.target); } });
    }, { threshold: 0.15 });
    reveals.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const ATTRACTIONS = [
    { key: 'cheileBorzesti', tag: t('home.attractions.tags.nature') },
    { key: 'cheileTurzii', tag: t('home.attractions.tags.hike') },
    { key: 'cascadaCiucas', tag: t('home.attractions.tags.waterfall') },
    { key: 'piatraSecuiului', tag: t('home.attractions.tags.viewpoint') },
    { key: 'munteleMare', tag: t('home.attractions.tags.peak') },
    { key: 'salinaTurda', tag: t('home.attractions.tags.unique') },
    { key: 'rimetea', tag: t('home.attractions.tags.village') },
    { key: 'cetateaColtesti', tag: t('home.attractions.tags.history') },
    { key: 'woodenChurchBuru', tag: t('home.attractions.tags.culture') },
    { key: 'ethnographicMuseumRimetea', tag: t('home.attractions.tags.museum') },
  ];

  const AttractionVisual = ({ variant = 1 }) => {
    return (
      <svg className={`home__attractions-visual home__attractions-visual--${variant}`} viewBox="0 0 800 500" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
        <defs>
          <linearGradient id={`atg-${variant}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="var(--h-primary)" stopOpacity="0.9" />
            <stop offset="1" stopColor="var(--h-teal)" stopOpacity="0.9" />
          </linearGradient>
          <radialGradient id={`atr-${variant}`} cx="30%" cy="25%" r="70%">
            <stop offset="0" stopColor="#ffffff" stopOpacity="0.35" />
            <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
          </radialGradient>
        </defs>
        <rect width="800" height="500" fill={`url(#atg-${variant})`} />
        <circle cx="220" cy="160" r="180" fill={`url(#atr-${variant})`} />
        <path d="M0 360 C120 330 210 270 320 300 C420 330 520 240 650 270 C720 285 770 320 800 340 L800 500 L0 500 Z" fill="rgba(10, 18, 28, 0.38)" />
        <path d="M0 395 C140 365 260 310 360 335 C460 360 540 300 670 330 C740 345 780 370 800 385 L800 500 L0 500 Z" fill="rgba(10, 18, 28, 0.26)" />
        <path d="M0 430 C160 410 280 370 390 390 C500 410 575 360 700 385 C760 397 790 410 800 418 L800 500 L0 500 Z" fill="rgba(10, 18, 28, 0.18)" />
      </svg>
    );
  };

  return (
    <div className="home page-enter">
      {/* ═══ HERO ═══ */}
      <section className="home__hero">
        {/* Background layers */}
        <div className="home__hero-bg">
          <div className="home__hero-gradient" />
          <div className="home__hero-pattern" />
          <div className="home__hero-mist home__hero-mist--1" />
          <div className="home__hero-mist home__hero-mist--2" />
        </div>

        {/* Animated floating particles */}
        <div className="home__hero-particles">
          {[...Array(20)].map((_, i) => (
            <span key={i} className="home__hero-particle" style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 8}s`,
              animationDuration: `${6 + Math.random() * 8}s`,
              width: `${2 + Math.random() * 4}px`,
              height: `${2 + Math.random() * 4}px`,
              opacity: 0.15 + Math.random() * 0.3,
            }} />
          ))}
        </div>

        {/* Mountain silhouettes */}
        <div className="home__hero-mountains">
          <svg className="home__hero-mountain home__hero-mountain--back" viewBox="0 0 1440 320" preserveAspectRatio="none">
            <path d="M0,320 L0,200 Q120,80 240,160 Q360,60 480,140 Q600,40 720,120 Q840,20 960,100 Q1080,30 1200,110 Q1320,50 1440,130 L1440,320 Z" />
          </svg>
          <svg className="home__hero-mountain home__hero-mountain--mid" viewBox="0 0 1440 320" preserveAspectRatio="none">
            <path d="M0,320 L0,220 Q180,120 360,200 Q480,100 600,180 Q720,80 840,170 Q960,90 1080,160 Q1200,100 1440,180 L1440,320 Z" />
          </svg>
          <svg className="home__hero-mountain home__hero-mountain--front" viewBox="0 0 1440 320" preserveAspectRatio="none">
            <path d="M0,320 L0,260 Q200,180 400,240 Q550,170 700,230 Q850,160 1000,220 Q1150,170 1300,210 Q1380,200 1440,240 L1440,320 Z" />
          </svg>
        </div>

        {/* Aurora / light bands */}
        <div className="home__hero-aurora">
          <div className="home__hero-aurora-band home__hero-aurora-band--1" />
          <div className="home__hero-aurora-band home__hero-aurora-band--2" />
          <div className="home__hero-aurora-band home__hero-aurora-band--3" />
        </div>

        {/* Decorative leaf shapes */}
        <div className="home__hero-leaf home__hero-leaf--1" />
        <div className="home__hero-leaf home__hero-leaf--2" />
        <div className="home__hero-leaf home__hero-leaf--3" />

        {/* Main content */}
        <div className="home__hero-content" data-reveal>
          {/* Tagline */}
          <div className="home__hero-tagline">
            <span className="home__hero-tagline-line" />
            <span className="home__hero-tagline-text">{t('home.hero.tagline')}</span>
            <span className="home__hero-tagline-line" />
          </div>

          {/* Title */}
          <h1 className="home__hero-title">
            <span className="home__hero-title-main">{t('home.hero.titleMain')}</span>
            <span className="home__hero-title-accent">{t('home.hero.titleAccent')}</span>
          </h1>

          {/* Ornamental line */}
          <div className="home__hero-ornament">
            <span className="home__hero-ornament-wing home__hero-ornament-wing--left" />
            <span className="home__hero-ornament-diamond" />
            <span className="home__hero-ornament-wing home__hero-ornament-wing--right" />
          </div>

          {/* Subtitle */}
          <p className="home__hero-subtitle" dangerouslySetInnerHTML={{ __html: t('home.hero.subtitle') }} />

          {/* Info chips */}
          <div className="home__hero-chips">
            <span className="home__hero-chip">{t('home.hero.chipLocation')}</span>
            <span className="home__hero-chip-sep" />
            <span className="home__hero-chip">{t('home.hero.chipType')}</span>
            <span className="home__hero-chip-sep" />
            <span className="home__hero-chip">{t('home.hero.chipEst')}</span>
          </div>

          {/* Buttons — vertical stack */}
          <div className="home__hero-actions">
            <Link to="/rent" className="home__hero-cta">
              <span className="home__hero-cta-text">{t('home.hero.cta')}</span>
              <span className="home__hero-cta-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
              </span>
            </Link>
            <Link to="/gallery" className="home__hero-link">
              {t('home.hero.viewGallery')}
              <span className="home__hero-link-line" />
            </Link>
          </div>

          {/* Guest rating */}
          <div className="home__hero-rating">
            <div className="home__hero-rating-stars">★★★★★</div>
            <span className="home__hero-rating-text">{t('home.hero.rating')}</span>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="home__hero-scroll">
          <div className="home__hero-scroll-mouse">
            <span className="home__hero-scroll-wheel" />
          </div>
          <span className="home__hero-scroll-text">{t('home.hero.scroll')}</span>
          <span className="home__hero-scroll-chevrons">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="6 9 12 15 18 9"/></svg>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="6 9 12 15 18 9"/></svg>
          </span>
        </div>
      </section>

      {/* ═══ STATS BAR ═══ */}
      <section className="home__stats">
        <div className="home__stats-inner">
          {STATS.map((s, i) => (
            <div key={i} className="home__stat" data-reveal style={{ transitionDelay: `${i * 0.1}s` }}>
              <span className="home__stat-value">{s.value}</span>
              <span className="home__stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ STORY ═══ */}
      <section className="home__story">
        <div className="home__story-inner">
          <div className="home__section-label" data-reveal>
            <span className="home__section-label-line" />
            <span>{t('home.story.label')}</span>
            <span className="home__section-label-line" />
          </div>
          <h2 className="home__story-title" data-reveal dangerouslySetInnerHTML={{ __html: t('home.story.title') }} />
          <div className="home__story-cols" data-reveal>
            <p className="home__story-text">
              {t('home.story.text1')}
            </p>
            <p className="home__story-text">
              {t('home.story.text2')}
            </p>
          </div>
          <div className="home__ornament" data-reveal>
            <span className="home__ornament-line" />
            <span className="home__ornament-diamond" />
            <span className="home__ornament-line" />
          </div>
        </div>
      </section>

      {/* ═══ AMENITIES ═══ */}
      <section className="home__amenities">
        {/* Subtle background ornamentation */}
        <div className="home__amenities-bg">
          <div className="home__amenities-bg-line home__amenities-bg-line--1" />
          <div className="home__amenities-bg-line home__amenities-bg-line--2" />
          <div className="home__amenities-bg-circle home__amenities-bg-circle--1" />
          <div className="home__amenities-bg-circle home__amenities-bg-circle--2" />
        </div>

        <div className="home__amenities-inner">
          {/* Section header */}
          <div className="home__amenities-header" data-reveal>
            <div className="home__section-label">
              <span className="home__section-label-line" />
              <span>{t('home.amenities.label')}</span>
              <span className="home__section-label-line" />
            </div>
            <h2 className="home__amenities-title" dangerouslySetInnerHTML={{ __html: t('home.amenities.title') }} />
            <p className="home__amenities-subtitle">
              {t('home.amenities.subtitle')}
            </p>
          </div>

          {/* Feature showcase — large left + stacked right */}
          <div className="home__amenities-showcase" data-reveal>
            <div className="home__amenity-feature">
              <div className="home__amenity-feature-visual">
                <svg className="home__amenity-feature-svg" viewBox="0 0 26 26" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d={AMENITIES[0].svg} />
                </svg>
                <div className="home__amenity-feature-badge">01</div>
              </div>
              <div className="home__amenity-feature-text">
                <h3 className="home__amenity-feature-title">{AMENITIES[0].title}</h3>
                <p className="home__amenity-feature-desc">{AMENITIES[0].desc}</p>
                <p className="home__amenity-feature-detail">{AMENITIES[0].detail}</p>
              </div>
            </div>
            <div className="home__amenity-stack">
              {AMENITIES.slice(1, 3).map((a, i) => (
                <div key={i} className="home__amenity-compact" style={{ transitionDelay: `${(i + 1) * 0.1}s` }}>
                  <div className="home__amenity-compact-icon">
                    <svg viewBox="0 0 26 26" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d={a.svg} />
                    </svg>
                  </div>
                  <div className="home__amenity-compact-text">
                    <span className="home__amenity-compact-num">{String(i + 2).padStart(2, '0')}</span>
                    <h4 className="home__amenity-compact-title">{a.title}</h4>
                    <p className="home__amenity-compact-desc">{a.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Decorative divider */}
          <div className="home__amenities-divider" data-reveal>
            <span className="home__amenities-divider-line" />
            <span className="home__amenities-divider-diamond" />
            <span className="home__amenities-divider-line" />
          </div>

          {/* Bottom row — 3 cards */}
          <div className="home__amenities-row" data-reveal>
            {AMENITIES.slice(3).map((a, i) => (
              <div key={i} className="home__amenity-card" style={{ transitionDelay: `${i * 0.12}s` }}>
                <div className="home__amenity-card-top">
                  <div className="home__amenity-card-icon">
                    <svg viewBox="0 0 26 26" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d={a.svg} />
                    </svg>
                  </div>
                  <span className="home__amenity-card-num">{String(i + 4).padStart(2, '0')}</span>
                </div>
                <h4 className="home__amenity-card-title">{a.title}</h4>
                <p className="home__amenity-card-desc">{a.desc}</p>
                <div className="home__amenity-card-accent" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ EXPERIENCE ═══ */}
      <section className="home__experience">
        <div className="home__experience-inner">
          <div className="home__experience-left" data-reveal="left">
            <div className="home__experience-visual">
              <div className="home__experience-visual-card" />
              <div className="home__experience-visual-accent" />
            </div>
          </div>
          <div className="home__experience-right" data-reveal="right">
            <div className="home__section-label home__section-label--light">
              <span className="home__section-label-line" />
              <span>{t('home.experience.label')}</span>
            </div>
            <h2 className="home__experience-title">
              {t('home.experience.title1')}
              <br />
              <em>{t('home.experience.title2')}</em>
            </h2>
            <p className="home__experience-desc">
              {t('home.experience.desc')}
            </p>
            <ul className="home__experience-list">
              {EXPERIENCES.map((exp, i) => (
                <li key={i} className="home__experience-item">
                  <span className="home__experience-item-dot" />
                  <span>{exp}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ═══ AT A GLANCE ═══ */}
      <section className="home__glance">
        <div className="home__glance-bg">
          <div className="home__glance-bg-grad" />
        </div>
        <div className="home__glance-inner">
          <div className="home__glance-header" data-reveal>
            <div className="home__section-label">
              <span className="home__section-label-line" />
              <span>{t('home.glance.label')}</span>
              <span className="home__section-label-line" />
            </div>
            <h2 className="home__glance-title">
              {t('home.glance.title1')}
              <br />
              <em>{t('home.glance.title2')}</em>
            </h2>
          </div>

          <div className="home__glance-grid">
            {/* ── Left: Info table ── */}
            <div className="home__glance-table" data-reveal="left">
              <div className="home__glance-row">
                <div className="home__glance-row-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                </div>
                <div className="home__glance-row-text">
                  <span className="home__glance-row-label">{t('home.glance.propertyType.label')}</span>
                  <span className="home__glance-row-value">{t('home.glance.propertyType.value')}</span>
                </div>
              </div>
              <div className="home__glance-row">
                <div className="home__glance-row-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
                </div>
                <div className="home__glance-row-text">
                  <span className="home__glance-row-label">{t('home.glance.capacity.label')}</span>
                  <span className="home__glance-row-value">{t('home.glance.capacity.value')}</span>
                </div>
              </div>
              <div className="home__glance-row">
                <div className="home__glance-row-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="10" r="3"/><path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 10-16 0c0 3 2.7 7 8 11.7z"/></svg>
                </div>
                <div className="home__glance-row-text">
                  <span className="home__glance-row-label">{t('home.glance.location.label')}</span>
                  <span className="home__glance-row-value">{t('home.glance.location.value')}</span>
                </div>
              </div>
              <div className="home__glance-row">
                <div className="home__glance-row-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                </div>
                <div className="home__glance-row-text">
                  <span className="home__glance-row-label">{t('home.glance.checkInOut.label')}</span>
                  <span className="home__glance-row-value">{t('home.glance.checkInOut.value')}</span>
                </div>
              </div>
              <div className="home__glance-row">
                <div className="home__glance-row-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                </div>
                <div className="home__glance-row-text">
                  <span className="home__glance-row-label">{t('home.glance.availability.label')}</span>
                  <span className="home__glance-row-value">{t('home.glance.availability.value')}</span>
                </div>
              </div>
              <div className="home__glance-row">
                <div className="home__glance-row-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                </div>
                <div className="home__glance-row-text">
                  <span className="home__glance-row-label">{t('home.glance.cancellation.label')}</span>
                  <span className="home__glance-row-value">{t('home.glance.cancellation.value')}</span>
                </div>
              </div>
            </div>

            {/* ── Right: Highlights + CTA ── */}
            <div className="home__glance-side" data-reveal="right">
              <div className="home__glance-highlights">
                <h3 className="home__glance-highlights-title">{t('home.glance.highlights.title')}</h3>
                <ul className="home__glance-highlights-list">
                  {getTranslatedItems('home.glance.highlights.items', 8).map((item, i) => (
                    <li key={i} className="home__glance-highlight">
                      <svg className="home__glance-highlight-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="home__glance-cta-card">
                <div className="home__glance-cta-text">
                  <span className="home__glance-cta-label">{t('home.glance.ctaLabel')}</span>
                  <span className="home__glance-cta-price">{t('home.glance.ctaPrice')}<span>{t('home.glance.ctaPerNight')}</span></span>
                </div>
                <Link to="/book" className="home__glance-cta-btn">
                  {t('home.glance.ctaButton')}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ NEARBY ATTRACTIONS ═══ */}
      <section className="home__attractions">
        <div className="home__attractions-inner">
          <div className="home__attractions-header" data-reveal>
            <div className="home__section-label">
              <span className="home__section-label-line" />
              <span>{t('home.attractions.label')}</span>
              <span className="home__section-label-line" />
            </div>
            <h2 className="home__attractions-title">
              {t('home.attractions.title1')}
              <br />
              <em>{t('home.attractions.title2')}</em>
            </h2>
            <p className="home__attractions-subtitle">{t('home.attractions.subtitle')}</p>
          </div>

          <div className="home__attractions-grid" data-reveal>
            {ATTRACTIONS.map((a, i) => (
              <article key={a.key} className="home__attractions-card" style={{ transitionDelay: `${i * 0.06}s` }}>
                <div className="home__attractions-media" role="img" aria-label={t(`home.attractions.items.${a.key}.name`)}>
                  <AttractionVisual variant={(i % 5) + 1} />
                  <span className="home__attractions-tag">{a.tag}</span>
                </div>
                <div className="home__attractions-body">
                  <h3 className="home__attractions-name">{t(`home.attractions.items.${a.key}.name`)}</h3>
                  <p className="home__attractions-desc">{t(`home.attractions.items.${a.key}.desc`)}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section className="home__cta">
        <div className="home__cta-floating">
          <span className="home__cta-orb home__cta-orb--1" />
          <span className="home__cta-orb home__cta-orb--2" />
          <span className="home__cta-orb home__cta-orb--3" />
        </div>
        <div className="home__cta-inner" data-reveal>
          <div className="home__cta-badge">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L14.4 8.6L21.5 9.2L16 14L17.5 21L12 17.5L6.5 21L8 14L2.5 9.2L9.6 8.6L12 2Z"/></svg>
            <span>{t('home.cta.badge')}</span>
          </div>
          <h2 className="home__cta-title">
            {t('home.cta.title1')}
            <br />
            <em>{t('home.cta.title2')}</em>
          </h2>
          <p className="home__cta-desc">
            {t('home.cta.desc')}
          </p>
          <div className="home__cta-btn-wrap">
            <span className="home__cta-pulse" />
            <span className="home__cta-pulse home__cta-pulse--delayed" />
            <Link to="/rent" className="home__cta-btn">
              <span>{t('home.cta.button')}</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </Link>
          </div>
          <p className="home__cta-reassurance">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            {t('home.cta.reassurance')}
          </p>
        </div>
      </section>

    </div>
  );
}